import React, { useState, useEffect, useRef } from 'react';
import { lastMouseClick } from 'utils/mouseTracker';
import { createPortal } from "react-dom";
import './BaseContextMenu.scss'

export interface BaseContextMenuProps {
  children: React.ReactNode;
  onClose: () => void;
  additionalClassName?: string;
  menuClassName?: string;
}

const BaseContextMenu = ({
  children,
  onClose,
  additionalClassName = '',
  menuClassName = '',
}: BaseContextMenuProps) => {
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: -1,
    y: -1,
  });

  const handleClose = () => {
    if (onClose) onClose();
  };

  useEffect(() => {
    setPosition({
      x: lastMouseClick.x,
      y: lastMouseClick.y,
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation?.();
        event.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  useEffect(() => {
    const handlePointerDownOutside = (e: PointerEvent) => {
      const target = e.target as Node;
      const targetElement =
        target instanceof Element
          ? target
          : target.parentNode instanceof Element
            ? target.parentNode
            : null;

      if (targetElement?.closest('[data-context-menu-trigger="true"]')) {
        return;
      }

      const isClickOutsideContextMenu =
        contextMenuRef.current && !contextMenuRef.current.contains(target);

      const isInsideModal = document
        .querySelector('.modal-container')
        ?.contains(target);

      if (isClickOutsideContextMenu && !isInsideModal) {
        handleClose();
      }
    };

    window.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () =>
      window.removeEventListener('pointerdown', handlePointerDownOutside, true);
  }, [handleClose]);

  useEffect(() => {
    if (!contextMenuRef.current) return;

    const { offsetWidth: contextMenuWidth, offsetHeight: contextMenuHeight } =
      contextMenuRef.current;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = position.x;
    let adjustedY = position.y;

    if (position.x + contextMenuWidth > viewportWidth) {
      adjustedX = viewportWidth - contextMenuWidth - 10;
    }
    if (position.y + contextMenuHeight > viewportHeight) {
      adjustedY = viewportHeight - contextMenuHeight - 40;
    } // 여기 여기여기

    // update있다면
    if (adjustedX !== position.x || adjustedY !== position.y) {
      setPosition({ x: adjustedX, y: adjustedY });
    }
  }, [position]);

  return (
    position.x !== -1 &&
    position.y !== -1 &&
    createPortal(
      <div
        className={
          menuClassName || `base-context-menu ${additionalClassName ?? ''}`
        }
        ref={contextMenuRef}
        style={{
          position: 'fixed',
          top: position.y,
          left: position.x,
          zIndex: 10000,
        }}
      >
        {children}
      </div>,
      document.body,
    )
  );
};

export default BaseContextMenu;
